/**
 * Non-breaking typed envelope adapter over the existing axios instance
 * (services/api.ts) — Story #5351 W1-T8.
 *
 * Existing call sites are completely untouched: this file adds no new
 * interceptor and does not modify apiClient. It reuses apiClient's own
 * per-request baseURL resolution (getApiBaseUrl(), re-run on every request
 * because config.js can load after module init), Bearer-token attach, and
 * the getIsLoggingOut 401 guard (services/api.ts's response interceptor) —
 * every call here goes through the exact same axios instance every existing
 * page already uses. New W1 code calls readEnvelope/writeEnvelope instead of
 * apiClient directly; legacy pages keep calling apiClient/the *Api objects
 * until a later wave migrates them.
 */
import type { AxiosError, AxiosResponse } from 'axios';
import apiClient from './api';
import type { TargetSet } from '../contracts/targetset';
import type {
  ReadResponse, ReadStatus, WriteRequest, WriteResponse, EnvelopeFailure,
} from '../contracts/envelope';

function genCorrelationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * failure_rules row, applied: classify a raw axios error into the one
 * EnvelopeFailure the caller actually needs to render — never let a
 * malformed/HTML/timeout/denied/provider-error response be mistaken for an
 * empty or healthy result (FAIL-03).
 */
export function classifyFailure(error: unknown): EnvelopeFailure {
  const axiosErr = error as AxiosError;
  if (!axiosErr?.response) {
    // No response at all — connection refused/timed out/DNS failure. A real
    // network failure, not a server-produced error body.
    return { kind: 'timeout', message: axiosErr?.message || 'no response received from the server' };
  }

  const { status, data } = axiosErr.response;
  const body = data as Record<string, unknown> | string | null | undefined;
  const bodyMessage = (typeof body === 'object' && body !== null)
    ? String(body.message ?? body.error ?? '')
    : undefined;

  if (typeof body === 'string' && body.trim().startsWith('<')) {
    // A raw HTML error page (proxy/gateway failure) must never be read as JSON data.
    return { kind: 'malformed', message: 'received an HTML error page instead of JSON' };
  }
  if (status === 401 || status === 403) {
    return { kind: 'denied', message: bodyMessage || 'access denied' };
  }
  if (status === 409) {
    return {
      kind: 'conflict',
      message: bodyMessage || 'version conflict',
      currentVersion: typeof body === 'object' && body !== null ? (body.current_version as string | undefined) : undefined,
      commandId: typeof body === 'object' && body !== null ? (body.command_id as string | undefined) : undefined,
    };
  }
  if (status >= 500) {
    return { kind: 'provider_error', message: bodyMessage || `upstream error (${status})` };
  }
  return { kind: 'malformed', message: bodyMessage || `unexpected response (${status})` };
}

function statusForFailure(failure: EnvelopeFailure): ReadStatus {
  switch (failure.kind) {
    case 'denied': return 'unauthorised';
    case 'timeout': return 'unreachable';
    default: return 'unknown';
  }
}

interface ReadEnvelopeOptions<T> {
  params?: Record<string, unknown>;
  /**
   * Existing handlers predate this contract and don't natively emit
   * `status` — this lets a caller classify freshness from the raw payload
   * shape until they do. Defaults to 'known' on a successful response,
   * matching today's un-enveloped behavior exactly (non-breaking).
   */
  deriveStatus?: (data: T) => ReadStatus;
}

/** Wraps a GET call in the ReadResponse envelope (Shared Contracts 'read_response' row). */
export async function readEnvelope<T>(
  path: string,
  scope: TargetSet,
  options?: ReadEnvelopeOptions<T>,
): Promise<ReadResponse<T>> {
  const observedAt = new Date().toISOString();
  try {
    const response: AxiosResponse<T> = await apiClient.get<T>(path, { params: options?.params });
    return {
      data: response.data,
      scope,
      source: path,
      observedAt,
      freshness: {},
      status: options?.deriveStatus ? options.deriveStatus(response.data) : 'known',
      schemaVersion: '1',
      correlationId: (response.headers?.['x-correlation-id'] as string | undefined) || genCorrelationId(),
    };
  } catch (error) {
    const failure = classifyFailure(error);
    return {
      data: null as unknown as T,
      scope,
      source: path,
      observedAt,
      freshness: {},
      status: statusForFailure(failure),
      schemaVersion: '1',
      correlationId: genCorrelationId(),
    };
  }
}

/**
 * Wraps a POST call in the WriteRequest/WriteResponse envelope (Shared
 * Contracts 'write_request'/'write_response' rows). Returns the classified
 * EnvelopeFailure on error rather than throwing, so callers render it
 * through StatePanel/UnavailableReason instead of an unhandled rejection.
 */
export async function writeEnvelope<P>(
  path: string,
  request: WriteRequest<P>,
): Promise<WriteResponse | EnvelopeFailure> {
  try {
    const response = await apiClient.post<WriteResponse>(path, request);
    return response.data;
  } catch (error) {
    return classifyFailure(error);
  }
}
